import { css } from '@emotion/react'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import { CalendarDate } from '@mathflat/shared/@modules/(dto)/CalendarDate'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { tJosa } from '~/@app/i18n'
import { casApi } from '~/@common/api/casApi'
import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { typo } from '~/@common/styles'
import _Switch from '~/@common/ui/_Switch/_Switch'
import Button from '~/@common/ui/(Button)/Button/Button'
import Calendar, { type CalendarDateRange } from '~/@common/ui/Calendar/Calendar'
import { Icon } from '~/@common/ui/Icon/Icon'
import Modal from '~/@common/ui/modal/Modal'

import { CasListMobileView } from './@widgets/list/CasListMobileView'

type Homework = CasAssignmentDtoT.AssignedCasWithHomework
type StatusTab = 'ALL' | Homework['status']

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'ALL', label: '전체' },
  { value: 'NOT_STARTED', label: '응시가능' },
  { value: 'IN_PROGRESS', label: '응시중' },
  { value: 'COMPLETED', label: '응시완료' },
]

const STATUS_LABEL: Record<Homework['status'], string> = {
  NOT_STARTED: '응시가능',
  IN_PROGRESS: '응시중',
  COMPLETED: '응시완료',
}

const STATUS_COLOR: Record<Homework['status'], string> = {
  NOT_STARTED: 'var(--Colors-Blue-600)',
  IN_PROGRESS: 'var(--Colors-Gray-800)',
  COMPLETED: 'var(--Colors-Gray-600)',
}

const buildInitialRange = (): CalendarDateRange => {
  const today = new CalendarDate()
  return {
    start: today.getPrevMonth(),
    end: today,
  }
}

const CasListPage = () => {
  const studentId = commonRepo.studentId
  const navigate = useNavigate()
  const { isMobile } = useStudentAppMediaQuery()
  const [dateRange, setDateRange] = useState<CalendarDateRange>(buildInitialRange)
  const [onlyHomework, setOnlyHomework] = useState(false)
  const [statusTab, setStatusTab] = useState<StatusTab>('ALL')

  const startDate = dateRange.start?.dateObj ? format(dateRange.start.dateObj, 'yyyy-MM-dd') : null
  const endDate = dateRange.end?.dateObj ? format(dateRange.end.dateObj, 'yyyy-MM-dd') : null

  const { data: rows = [] } = useQuery({
    queryKey: ['casHomeworks', studentId, startDate, endDate],
    queryFn: async () => {
      if (!studentId || !startDate || !endDate) return [] as Homework[]
      return await casApi.getHomeworks(studentId, { startDate, endDate })
    },
    enabled: !!studentId && !!startDate && !!endDate,
    refetchOnMount: 'always',
  })

  const filtered = rows
    .filter((r) => (onlyHomework ? r.homeworks.length > 0 : true))
    .filter((r) => (statusTab === 'ALL' ? true : r.status === statusTab))

  const dateRangeLabel =
    dateRange.start?.dateObj && dateRange.end?.dateObj
      ? `${format(dateRange.start.dateObj, 'yyyy.MM.dd')}~${format(dateRange.end.dateObj, 'yyyy.MM.dd')}`
      : ''

  const openCalendar = () => {
    modalService.openModal(
      <CalendarRangeModal selectedRange={dateRange} onConfirm={setDateRange} />,
      { modalName: 'cas-calendar', hasCloseButton: true },
    )
  }

  const handleRowClick = (row: Homework) => {
    if (row.accessModifierToStudent === 'PRIVATE') {
      modalService.openModal(
        <Modal.Alert
          confirm={{
            onClick: () => {
              modalService.closeModal()
            },
          }}
        >
          선생님이 비공개 한 {tJosa('반응형평가')}입니다.
          <br />
          공개하기 전까지는 볼 수 없어요!
        </Modal.Alert>,
        { modalName: '비공개 자동시험 안내 모달' },
      )
      return
    }
    navigate(
      generatePath(routeName.student.casByAssignedId, {
        assignedId: String(row.assignedId),
      }),
    )
  }

  if (isMobile) {
    return (
      <CasListMobileView
        rows={filtered}
        statusTab={statusTab}
        dateRange={dateRange}
        onlyHomework={onlyHomework}
        dateRangeLabel={dateRangeLabel}
        onChangeStatusTab={setStatusTab}
        onChangeFilters={({ dateRange: nextRange, onlyHomework: nextOnlyHomework }) => {
          setDateRange(nextRange)
          setOnlyHomework(nextOnlyHomework)
        }}
        onRowClick={handleRowClick}
      />
    )
  }

  return (
    <div css={S.page}>
      <div css={S.filterRow}>
        <button type="button" css={S.dateChip} onClick={openCalendar}>
          <p>{dateRangeLabel}</p>
          <Icon name="icon_calendar" size={20} />
        </button>
        <_Switch checked={onlyHomework} onChange={() => setOnlyHomework((v) => !v)}>
          숙제만 보기
        </_Switch>
      </div>

      <div css={S.subTabs}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            css={S.tab(statusTab === tab.value)}
            onClick={() => setStatusTab(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div css={S.tableWrap}>
        <div css={S.tableHeader}>
          <span css={S.iconCol} />
          <span css={S.colDate}>{tJosa('출제')}일</span>
          <span css={S.colStatus}>상태</span>
          <span css={S.colTitle}>시험지명</span>
          <span css={S.colResult}>결과</span>
        </div>
        {filtered.map((row) => (
          <div key={row.assignedId} css={S.row} onClick={() => handleRowClick(row)}>
            <span css={S.iconCol}>
              <Icon name="icon_description_paper" size={20} />
            </span>
            <span css={S.colDate}>{format(new Date(row.assignDatetime), 'yy.MM.dd')}</span>
            <span
              css={[
                S.colStatus,
                css`
                  color: ${STATUS_COLOR[row.status]};
                `,
              ]}
            >
              {STATUS_LABEL[row.status]}
            </span>
            <div css={S.colTitle}>
              <div className="title">{row.title}</div>
              <div className="meta">
                {row.homeworks.length > 0 && (
                  <>
                    <span>숙제</span>
                    <i />
                  </>
                )}
                {row.autoScored && (
                  <>
                    <span>자동채점</span>
                    <i />
                  </>
                )}
                {row.accessModifierToStudent === 'PRIVATE' && (
                  <>
                    <span>비공개</span>
                    <i />
                  </>
                )}
                <span>{row.maxProblemCount}문제</span>
              </div>
            </div>
            <div css={S.colResult}>
              {row.status === 'COMPLETED' ? (
                <div css={S.resultBox}>
                  <div css={S.scoreText}>{row.score ?? 0}점</div>
                  <div css={S.countRow}>
                    <span css={S.countItem}>✕ : {row.wrongCount}개</span>
                    <span css={S.countItem}>○ : {row.correctCount}개</span>
                  </div>
                </div>
              ) : row.status === 'NOT_STARTED' ? (
                <span css={S.resultMuted}>응시 전</span>
              ) : (
                <span css={S.resultMuted}>응시완료 후 공개됩니다.</span>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div css={S.empty}>
            {tJosa('출제')}된 {tJosa('반응형평가', '이/가')} 없어요.
          </div>
        )}
      </div>
    </div>
  )
}

export default CasListPage

const CalendarRangeModal = ({
  selectedRange,
  onConfirm,
}: {
  selectedRange: CalendarDateRange
  onConfirm: (range: CalendarDateRange) => void
}) => {
  const [local, setLocal] = useState(selectedRange)
  return (
    <div>
      <strong
        css={css`
          color: var(--Colors-Gray-900);
          ${typo.body01};
        `}
      >
        날짜 선택
      </strong>
      <Calendar
        style={{ marginTop: '12px', width: '312px', height: '288px' }}
        type="range"
        selectedRange={local}
        onRangeSelect={setLocal}
      />
      <Button
        size="small"
        css={css`
          width: 100%;
          border-radius: var(--Radius-200);
          color: var(--Colors-Blue-600);
          background-color: #e7eaf8;
          margin-top: 12px;
        `}
        onClick={() => {
          onConfirm(local)
          modalService.closeModal()
        }}
      >
        확인
      </Button>
    </div>
  )
}

const S = {
  page: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 100%;
    background: #f0f2f4;
  `,
  filterRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  dateChip: css`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border-radius: 6px;
    color: var(--Colors-Gray-800);
    p {
      ${typo.body02};
    }
  `,
  subTabs: css`
    display: flex;
    gap: 8px;
    align-self: center;
    width: 384px;
  `,
  tab: (active: boolean) => css`
    flex: 1;
    max-width: 90px;
    padding: 10px 16px;
    border-radius: 8px;
    border: ${active ? 'none' : `1px solid var(--Colors-Gray-300)`};
    background: ${active ? 'var(--Colors-Blue-600)' : 'var(--White)'};
    color: ${active ? 'var(--White)' : 'var(--Colors-Gray-900)'};
    font-size: 12px;
    line-height: 18px;
  `,
  tableWrap: css`
    flex: 1;
    background: var(--White);
    border-radius: 14px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  `,
  tableHeader: css`
    display: flex;
    align-items: center;
    padding: 16px 14px;
    background: var(--Colors-Gray-100);
    color: var(--Colors-Gray-600);
    ${typo.body02};
    font-weight: 700;
  `,
  iconCol: css`
    width: 30px;
    display: flex;
    justify-content: center;
  `,
  colDate: css`
    width: 80px;
    text-align: center;
  `,
  colStatus: css`
    width: 80px;
    text-align: center;
  `,
  colTitle: css`
    flex: 1;
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .title {
      ${typo.body02};
      color: var(--Colors-Gray-900);
    }
    .meta {
      display: flex;
      align-items: center;
      gap: 6px;
      ${typo.caption01};
      color: var(--Colors-Gray-700);
      i {
        display: inline-block;
        width: 1px;
        height: 10px;
        background: var(--Colors-Gray-500);
      }
    }
  `,
  colResult: css`
    width: 176px;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  row: css`
    display: flex;
    align-items: center;
    padding: 14px;
    cursor: pointer;
    border-bottom: 1px solid var(--Colors-Gray-200);
    &:last-of-type {
      border-bottom: none;
    }
    &:hover {
      background: var(--Colors-Gray-100);
    }
  `,
  resultMuted: css`
    color: var(--Colors-Gray-500);
    ${typo.caption01};
  `,
  resultBox: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
  `,
  scoreText: css`
    ${typo.caption01};
    font-weight: 700;
    color: var(--Colors-Gray-800);
  `,
  countRow: css`
    display: flex;
    gap: 10px;
  `,
  countItem: css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    ${typo.caption01};
    color: var(--Colors-Gray-700);
  `,
  empty: css`
    flex: 1;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--Colors-Gray-500);
    ${typo.body02};
  `,
}
